import React, { useMemo } from 'react';
import { t } from 'i18next';
import { toast } from 'react-toastify';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { ErrorMessage, Field, Formik, Form as FormikForm } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import RequiredAstric from '../../../components/DynamicForm/required-astric';
import { setMedicienDetails } from '../../../redux/mediciens/slice';
import { createMediciens, updateMediciens } from '../../../redux/mediciens';
import { useGetMedicineTypeOptions } from './hooks';
import { getMediciensValidationSchema } from './validation';


const MediciensModel = ({ show, close, fetchMedicines }) => {
	const { medicienDetails } = useSelector(state => state.mediciens);
	const dispatch = useDispatch();
	const isEdit = useMemo(() => Boolean(medicienDetails?.id), [medicienDetails]);
	const typeOptions = useGetMedicineTypeOptions({ t });

	const handleClose = () => {
		close();
		dispatch(setMedicienDetails(null));
	};

	const handleSubmit = async(values, { setSubmitting, resetForm }) => {
		try {
			const action = isEdit && medicienDetails.id
				? updateMediciens({ body: values, id: medicienDetails.id })
				: createMediciens(values);

			await dispatch(action).unwrap();
			handleClose();
			resetForm();
			fetchMedicines();
		} catch (error) {
			toast.error(error.message);
			return error;
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal show={show} onHide={handleClose} backdrop="static" centered >
			<Modal.Header closeButton>
				<Modal.Title>{t(`GENERAL.${isEdit ? 'UPDATE' : 'ADD'}`)} {t('MEDICIENS.NAV.MEDICINE')}</Modal.Title>
			</Modal.Header>

			<Formik
				initialValues={{
					name: medicienDetails?.name || '',
					genericName: medicienDetails?.genericName || '',
					description: medicienDetails?.description,
					type: medicienDetails?.type || '',
					minAge: medicienDetails?.minAge || '',
					maxAge: medicienDetails?.maxAge || '',
					status: medicienDetails?.status?.toLowerCase() || 'active',
				}}
				validationSchema={getMediciensValidationSchema(t)}
				enableReinitialize={true}
				onSubmit={handleSubmit}
			>
				{({ isSubmitting }) => (
					<FormikForm>
						<Modal.Body>
							<Row className="mb-3">
								<Form.Group>
									<Form.Label>{t('MEDICIENS.TABLE.MEDICINE_NAME')} <RequiredAstric /></Form.Label>
									<Field name="name" as={Form.Control} />
									<div className="text-danger small"><ErrorMessage name="name" /></div>
								</Form.Group>
							</Row>

							<Row className="mb-3">
								<Form.Group>
									<Form.Label>{t('MEDICIENS.TABLE.GENERIC_NAME')} <RequiredAstric /></Form.Label>
									<Field name="genericName" as={Form.Control} />
									<div className="text-danger small"><ErrorMessage name="genericName" /></div>
								</Form.Group>
							</Row>

							<Row className="mb-3">
								<Form.Group>
									<Form.Label>{t('MEDICIENS.TABLE.DESCRIPTION')}</Form.Label>
									<Field name="description" as={Form.Control} />
									<div className="text-danger small"><ErrorMessage name="description" /></div>
								</Form.Group>
							</Row>

							<Row className="mb-3">
								<Form.Group>
									<Form.Label>{t('MEDICIENS.TABLE.TYPE')} <RequiredAstric /></Form.Label>
									<Field name="type" as="select" className="form-select">
										{typeOptions.map((option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</Field>
									<div className="text-danger small"><ErrorMessage name="type" /></div>
								</Form.Group>
							</Row>

							<Row className="mb-3">
								<Col>
									<Form.Group>
										<Form.Label>{t('MEDICIENS.TABLE.MIN_AGE')} <RequiredAstric /></Form.Label>
										<Field name="minAge" type="number" as={Form.Control} />
										<div className="text-danger small"><ErrorMessage name="minAge" /></div>
									</Form.Group>
								</Col>
								<Col>
									<Form.Group>
										<Form.Label>{t('MEDICIENS.TABLE.MAX_AGE')} <RequiredAstric /></Form.Label>
										<Field name="maxAge" type="number" as={Form.Control} />
										<div className="text-danger small"><ErrorMessage name="maxAge" /></div>
									</Form.Group>
								</Col>
							</Row>

							<Row className="mb-3">
								<Form.Group>
									<Form.Label>{t('MEDICIENS.TABLE.STATUS')}</Form.Label>
									<Field name="status" as="select" className="form-select">
										<option value="active">{t('GENERAL.ACTIVE')}</option>
										<option value="inactive">{t('GENERAL.INACTIVE')}</option>
									</Field>
									<div className="text-danger small"><ErrorMessage name="status" /></div>
								</Form.Group>
							</Row>

							<Row className="mb-3">
								<Col>
									<Button type="submit" className='px-3 py-2 me-2' variant="primary" disabled={isSubmitting}>
										{isSubmitting ? t('GENERAL.SAVING') : isEdit ? t('GENERAL.UPDATE') : t('GENERAL.SUBMIT')}
									</Button>
									<Button variant="danger" className='px-3 py-2' onClick={handleClose}>{t('GENERAL.CANCEL')}</Button>
								</Col>
							</Row>
						</Modal.Body>
					</FormikForm>
				)}
			</Formik>
		</Modal >
	);
};

export default MediciensModel;
